/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';
import _ from 'underscore';

import * as OrganizationActions from 'actions/OrganizationActions';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import AvatarText from 'ui-library/avatar/AvatarText';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {readableTime} from 'util/DateUtil';

import {MemberRequest} from './OrganizationConstants';

const PENDING_MEMBERS_ROW_CONTENT_HEIGHT = 40; // 20px line-height, 2 lines
const MIN_TABLE_LENGTH = 3;

interface PendingMemberCellProps {
  pendingMember: {
    createdAt: string;
    email?: string;
    user?: {
      email: string;
    };
    userAccepted: any;
  };
}

const PendingMemberCell = ({pendingMember}: PendingMemberCellProps): JSX.Element => {
  const email = pendingMember.user ? pendingMember.user.email : pendingMember.email;
  const createdAt = readableTime(pendingMember.createdAt, {showYear: true, showTime: false});
  return (
    <div className="u-vertical-center-items">
      <AvatarText
        text={email!.charAt(0).toUpperCase()}
        size={AvatarSize.SMALL}
        backgroundColor={Colors.GRAY}
      />
      <div className="u-small-spacing-left">
        <div>{email}</div>
        <div className="u-faded">
          {`${pendingMember.userAccepted ? 'Requested to join on' : 'Invited'} ${createdAt}`}
        </div>
      </div>
    </div>
  );
};

interface RequestApprovalButtonsProps {
  organizationId: string;
  request: MemberRequest;
}

function RequestApprovalButtons({organizationId, request}: RequestApprovalButtonsProps): JSX.Element {
  return (
    <div>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        onClick={() => OrganizationActions.rejectInboundRequest(organizationId, request.user.id)}
        size={ButtonSize.SMALL}
        text="Reject Request"
      />
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        onClick={() => OrganizationActions.acceptInboundRequest(organizationId, request.user.id)}
        size={ButtonSize.SMALL}
        text="Approve Request"
        className="u-mini-spacing-left"
      />
    </div>
  );
}

interface CancelInviteButtonProps {
  organizationId: string;
  email: string;
}

function CancelInviteButton({organizationId, email}: CancelInviteButtonProps): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      onClick={() => OrganizationActions.cancelInvite(organizationId, email)}
      size={ButtonSize.SMALL}
      text="Cancel Invite"
    />
  );
}

export interface PendingMembersTableProps {
  className?: string;
  invites?: Array<any>; // TODO: OrganizationConstants.MEMBER_INVITE.isRequired
  canManageRoles: boolean;
  organizationId: string;
  requests?: Array<any>; // TODO: OrganizationConstants.MEMBER_REQUEST.isRequired
}

type PendingMembersTableState = any;

class PendingMembersTable extends React.Component<PendingMembersTableProps, PendingMembersTableState> {
  static defaultProps = {
    canManageRoles: false,
  };

  state: PendingMembersTableState = {
    showAllPending: false,
  };

  _getRowContentHeight = (): number => PENDING_MEMBERS_ROW_CONTENT_HEIGHT;

  _getEmail = (pendingMember: any): string =>
    pendingMember.user ? pendingMember.user.email : pendingMember.email;

  _renderPendingMemberButtons = (pendingMember: any): JSX.Element | null => {
    if (!this.props.canManageRoles) {
      return null;
    }
    // If the user sent the request, allow rejecting/approving.
    if (pendingMember.userAccepted) {
      return <RequestApprovalButtons organizationId={this.props.organizationId} request={pendingMember} />;
    }
    // Otherwise, just allow org admin to cancel the request.
    return (
      <CancelInviteButton organizationId={this.props.organizationId} email={this._getEmail(pendingMember)} />
    );
  };

  _renderPendingMemberCell = (pendingMember: any): JSX.Element => (
    <div className="u-lr-split">
      <PendingMemberCell pendingMember={pendingMember} />
      {this._renderPendingMemberButtons(pendingMember)}
    </div>
  );

  render(): JSX.Element | null {
    if (_.isEmpty(this.props.invites) && _.isEmpty(this.props.requests)) {
      return null;
    }

    const requestsGroupedByUserAccepted = _.groupBy(this.props.requests!, 'userAccepted');
    const userOrgsInboundRequests = requestsGroupedByUserAccepted.true || [];
    const currentUserInboundRequests = requestsGroupedByUserAccepted.false || [];
    const pendingMembers = [
      ...userOrgsInboundRequests,
      // TODO: Figure out if this use of ! is safe.
      ...this.props.invites!,
      ...currentUserInboundRequests,
    ];
    const pendingMembersSliced = this.state.showAllPending ? pendingMembers : pendingMembers.slice(0, 3);
    const columns = [
      {
        header: 'Pending Members',
        accessorKey: 'member',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: false,
      },
    ];
    const rows = pendingMembersSliced.map((member) => ({
      id: member.user ? member.user.email : member.email,
      data: {
        member: this._renderPendingMemberCell(member),
      },
      extra: {
        result: member,
      },
    }));

    return (
      <div className={this.props.className}>
        <div className="u-small-spacing-bottom">
          <ScrollableTable
            columns={columns}
            hasLineBelowHeader={false}
            rows={rows}
            getRowContentHeight={this._getRowContentHeight}
          />
        </div>
        {pendingMembers.length > MIN_TABLE_LENGTH && (
          <a
            onClick={() => {
              this.setState({showAllPending: !this.state.showAllPending});
            }}
            className="organizationPage-pendingMembersTable-showAllLink"
          >
            {this.state.showAllPending ? 'Hide' : `Show all ${pendingMembers.length} pending members`}
          </a>
        )}
      </div>
    );
  }
}

export default PendingMembersTable;
